<template>
    <button
        :data-cy="dataCy"
        class="a-button"
        :class="buttonClasses"
        :style="buttonStyles"
        :type="buttonType"
        @click.stop="$emit('clicked')"
    >
        <font-awesome-icon
            v-if="showIcon"
            class="icon"
            :icon="[iconOptions.iconStyle, iconOptions.iconName]"
        />
        <span v-if="showLabel" class="a-button-label">
            {{ label }}
        </span>
        <div
            v-if="routeTo && !loading"
            class="link-cover"
            @click="pushToRoute"
        ></div>
        <div v-if="loading" class="loading">
            <AonSpinner :scale="0.2" :color="loadColor" />
        </div>
    </button>
</template>

<script setup>
import { computed } from 'vue';
import AonSpinner from './AonSpinner.vue';

const props = defineProps({
    // This only applies to subtle for now - RR
    darkMode: {
        type: Boolean,
        default: false
    },
    // QA ancillary
    dataCy: {
        type: String,
        default: 'a-button'
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    iconOptions: {
        type: Object,
        default: () => { return { iconStyle: 'fal', iconName: 'fa-download' } },
        validator: (val) => {
            if (!val.iconStyle || !val.iconName) {
                // TODO probably import Logger from parent (make a peerDependency)
                console.error(
                    'Missing values for the iconOptions prop. ' +
                    `Be sure to include in the iconOptions object: iconStyle and iconName. Example: { iconStyle: 'fal', iconName: 'fa-download' }`
                )
            }
            return true
        },
    },
    isFormSubmit: {
        type: Boolean,
        default: false
    },
    label: {
        type: String,
        default: '',
    },
    loadColor: {
        type: String,
        default: 'white',
    },
    loading: {
        type: Boolean,
        default: false,
    },
    routeTo: {
        type: String,
        default: null,
    },
    textWithIcon: {
        type: Boolean,
        default: false
    },
    type: {
        type: String,
        default: 'default',
        validator: (val) => ['default', 'prominent', 'subtle', 'ghost', 'icon', 'transparent'].includes(val),
    },
    width: {
        type: String,
        default: '130px',
    },
})

const pushToRoute = () => {
    this.$router.push(props.routeTo)
}
const buttonType = computed(() => {
    return props.isFormSubmit ? 'submit' : 'button'
})
const buttonClasses = computed(() => {
    return [
        {
            'dark-mode': props.darkMode,
            disabled: (props.loading || props.disabled),
            'icon-text': props.textWithIcon,
        },
        props.type
    ]
})
const buttonStyles = computed(() => {
    return {
        'min-width': props.width
    }
})
const showIcon = computed(() => {
    return !props.loading && (props.type === 'icon' || props.textWithIcon)
})
const showLabel = computed(() => {
    return !props.loading && props.type !== 'icon'
})
</script>

<style lang="scss">
.a-button {
    position: relative;

    &.icon-text {
        .icon {
            width: 18px;
            height: 18px;
            margin-right: 5px;
        }
    }

    .link-cover {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 10;
    }
}
</style>
